"use strict";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import { FetchError } from "data/apiClients/FetchError";
import { Routing } from "data/tradingApi/__generated__/index";
import { updateSwapStartTimestamp } from "features/timing/slice";
import {
  HandledTransactionInterrupt,
  TransactionError,
  TransactionStepFailedError,
  UnexpectedTransactionStateError
} from "features/transactions/errors";
import { createApprovalTransactionStep } from "features/transactions/steps/approve";
import { TransactionStepType } from "features/transactions/steps/types";
import { useV4SwapEnabled } from "features/transactions/swap/hooks/useV4SwapEnabled";
import {
  createSwapTransactionStep
} from "features/transactions/swap/steps/swap";
import { generateSwapTransactionSteps } from "features/transactions/swap/utils/generateSwapTransactionSteps";
import { useAccount } from "hooks/useAccount";
import useSelectChain from "hooks/useSelectChain";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { handleAtomicSendCalls } from "state/sagas/transactions/5792";
import { AtomicBatchUnsupportedError } from "state/sagas/transactions/atomicBatch";
import { useGetOnPressRetry } from "state/sagas/transactions/retry";
import { handleUniswapXSignatureStep } from "state/sagas/transactions/uniswapx";
import {
  addTransactionBreadcrumb,
  getSwapTransactionInfo,
  handleApprovalTransactionStep,
  handleOnChainStep,
  handlePermitTransactionStep,
  handleSignatureStep
} from "state/sagas/transactions/utils";
import { handleWrapStep } from "state/sagas/transactions/wrapSaga";
import invariant from "tiny-invariant";
import { call } from "typed-redux-saga";
import { logger } from "utilities/src/logger/logger";
import { createSaga } from "utils/saga";
import { didUserReject } from "utils/swapErrorToUserReadableMessage";
function* handleSwapTransactionStep(params) {
  const { trade, step, signature } = params;
  const info = getSwapTransactionInfo(trade);
  const txRequest = yield* call(getSwapTxRequest, step, signature);
  const onChainStep = { ...step, txRequest };
  const hash = yield* call(handleOnChainStep, {
    ...params,
    info,
    step: onChainStep,
    ignoreInterrupt: true,
    // We avoid interruption during the swap step, since it is too late to give user a new trade once the swap is submitted.
    shouldWaitForConfirmation: false
  });
  popupRegistry.addPopup({ type: PopupType.Transaction, hash }, hash);
  return;
}
function* handleBatchFallback(params) {
  const { account, step, setCurrentStep, setSteps, trade } = params;
  const { batchedTxRequests } = step;
  if (batchedTxRequests.length === 0) {
    throw new UnexpectedTransactionStateError("Batched fallback: no txRequests to execute");
  }
  const swapTxRequest = batchedTxRequests[batchedTxRequests.length - 1];
  const preSwapTxRequests = batchedTxRequests.slice(0, -1);
  const approvalSteps = [];
  for (const txRequest of preSwapTxRequests) {
    const approvalStep = createApprovalTransactionStep(txRequest, trade.inputAmount);
    if (!approvalStep) {
      throw new UnexpectedTransactionStateError("Batched fallback: failed to synthesize approval step");
    }
    approvalSteps.push(approvalStep);
  }
  const swapStep = createSwapTransactionStep(swapTxRequest);
  setSteps([...approvalSteps, swapStep]);
  for (const approvalStep of approvalSteps) {
    try {
      yield* call(handleApprovalTransactionStep, { account, step: approvalStep, setCurrentStep });
    } catch (err) {
      if (didUserReject(err)) {
        throw err;
      }
      throw rewrapStepError(err, approvalStep);
    }
  }
  try {
    yield* call(handleSwapTransactionStep, { account, step: swapStep, setCurrentStep, trade });
  } catch (err) {
    if (didUserReject(err)) {
      throw err;
    }
    throw rewrapStepError(err, swapStep);
  }
}
function rewrapStepError(err, step) {
  if (err instanceof TransactionError) {
    return err;
  }
  if (err instanceof Error) {
    return new TransactionStepFailedError({
      message: `${step.type} failed during swap fallback`,
      step,
      originalError: err
    });
  }
  return err;
}
function* handleSwapTransactionBatchedStep(params) {
  const { trade, step } = params;
  const info = getSwapTransactionInfo(trade);
  const batchId = yield* handleAtomicSendCalls({
    ...params,
    info,
    step,
    ignoreInterrupt: true,
    // We avoid interruption during the swap step, since it is too late to give user a new trade once the swap is submitted.
    shouldWaitForConfirmation: false
  });
  popupRegistry.addPopup({ type: PopupType.Transaction, hash: batchId }, batchId);
  return;
}
function* getSwapTxRequest(step, signature) {
  if (step.type === TransactionStepType.SwapTransaction) {
    return step.txRequest;
  }
  if (!signature) {
    throw new UnexpectedTransactionStateError("Signature required for async swap transaction step");
  }
  const txRequest = yield* call(step.getTxRequest, signature);
  invariant(txRequest !== void 0);
  return txRequest;
}
function* swap(params) {
  const { swapTxContext, setSteps, selectChain, startChainId, v4Enabled, onFailure } = params;
  try {
    const steps = yield* call(generateSwapTransactionSteps, swapTxContext, v4Enabled);
    setSteps(steps);
    const swapChainId = swapTxContext.trade.inputAmount.currency.chainId;
    if (swapChainId !== startChainId) {
      const chainSwitched = yield* call(selectChain, swapChainId);
      if (!chainSwitched) {
        onFailure();
        return void 0;
      }
    }
    switch (swapTxContext.routing) {
      case Routing.CLASSIC:
      case Routing.BRIDGE:
        return yield* classicSwap({ ...params, swapTxContext, steps });
      case Routing.DUTCH_V2:
      case Routing.DUTCH_V3:
      case Routing.PRIORITY:
        return yield* uniswapXSwap({ ...params, swapTxContext, steps });
    }
  } catch (error) {
    logger.error(error, { tags: { file: "swapSaga", function: "swap" } });
    onFailure(error);
  }
}
function* classicSwap(params) {
  const {
    account,
    setCurrentStep,
    setSteps,
    steps,
    swapTxContext: { trade },
    onSuccess,
    onFailure
  } = params;
  let signature;
  for (const step of steps) {
    try {
      switch (step.type) {
        case TransactionStepType.TokenRevocationTransaction:
        case TransactionStepType.TokenApprovalTransaction: {
          yield* call(handleApprovalTransactionStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.Permit2Signature: {
          signature = yield* call(handleSignatureStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.Permit2Transaction: {
          yield* call(handlePermitTransactionStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.SwapTransaction:
        case TransactionStepType.SwapTransactionAsync: {
          yield* call(handleSwapTransactionStep, { account, signature, step, setCurrentStep, trade });
          break;
        }
        case TransactionStepType.SwapTransactionBatched: {
          try {
            yield* call(handleSwapTransactionBatchedStep, { account, step, setCurrentStep, trade });
          } catch (batchedError) {
            if (!(batchedError instanceof AtomicBatchUnsupportedError)) {
              throw batchedError;
            }
            logger.warn(
              "swapSaga",
              "classicSwap",
              `Atomic batch failed (${batchedError?.message ?? "unknown"}); falling back to sequential transactions`
            );
            yield* call(handleBatchFallback, { account, step, setCurrentStep, setSteps, trade });
          }
          break;
        }
        default: {
          throw new UnexpectedTransactionStateError(`Unexpected step type: ${step.type}`);
        }
      }
    } catch (error) {
      const displayableError = getDisplayableError(error, step);
      if (displayableError) {
        logger.error(displayableError, { tags: { file: "swapSaga", function: "classicSwap" } });
      }
      const onPressRetry = params.getOnPressRetry(displayableError);
      onFailure(displayableError, onPressRetry);
      return;
    }
  }
  yield* call(onSuccess);
}
function* uniswapXSwap(params) {
  const {
    account,
    setCurrentStep,
    steps,
    swapTxContext: { trade },
    onFailure,
    onSuccess
  } = params;
  for (const step of steps) {
    try {
      switch (step.type) {
        case TransactionStepType.WrapTransaction: {
          yield* call(handleWrapStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.TokenRevocationTransaction:
        case TransactionStepType.TokenApprovalTransaction: {
          yield* call(handleApprovalTransactionStep, { account, step, setCurrentStep });
          break;
        }
        case TransactionStepType.UniswapXSignature: {
          yield* call(handleUniswapXSignatureStep, { account, step, setCurrentStep, trade });
          break;
        }
        default: {
          throw new UnexpectedTransactionStateError(`Unexpected step type: ${step.type}`);
        }
      }
    } catch (error) {
      const displayableError = getDisplayableError(error, step);
      if (displayableError) {
        logger.error(displayableError, { tags: { file: "swapSaga", function: "uniswapXSwap" } });
      }
      onFailure(displayableError);
      return;
    }
  }
  yield* call(onSuccess);
}
function getDisplayableError(error, step) {
  const userRejected = didUserReject(error);
  if (userRejected || error instanceof HandledTransactionInterrupt) {
    const loggableMessage = userRejected ? "user rejected request" : error.message;
    addTransactionBreadcrumb({ step, status: "interrupted", data: { message: loggableMessage } });
    return void 0;
  } else if (error instanceof TransactionError) {
    return error;
  } else {
    const isBackendRejection = error instanceof FetchError;
    return new TransactionStepFailedError({
      message: `${step.type} failed during swap`,
      step,
      isBackendRejection,
      originalError: error
    });
  }
}
export const swapSaga = createSaga(swap, "swapSaga");
export function useSwapCallback() {
  const appDispatch = useDispatch();
  const selectChain = useSelectChain();
  const startChainId = useAccount().chainId;
  const v4SwapEnabled = useV4SwapEnabled(startChainId);
  const getOnPressRetry = useGetOnPressRetry();
  return useCallback(
    (args) => {
      const { account, swapTxContext, onSuccess, onFailure, setCurrentStep, setSteps } = args;
      try {
        const swapParams = {
          swapTxContext,
          account,
          getOnPressRetry,
          onSuccess,
          onFailure,
          setCurrentStep,
          setSteps,
          selectChain,
          startChainId,
          v4Enabled: v4SwapEnabled
        };
        appDispatch(swapSaga.actions.trigger(swapParams));
      } catch (error) {
        console.error("Error in useSwapCallback:", error);
        onFailure?.(error);
        return;
      }
      appDispatch(updateSwapStartTimestamp({ timestamp: void 0 }));
    },
    [selectChain, startChainId, v4SwapEnabled, appDispatch, getOnPressRetry]
  );
}
